import { inject, Injectable } from '@angular/core';
import * as forge from 'node-forge';
import { AsappChatConfig, AsappCustomerInfo } from '../../model/asapp-chat/asapp-chat.dto';
import { forkJoin } from 'rxjs';
import { CryptoResponseDto } from './cypto/crypto-response.dto';
import { PGHOktaService } from './pghokta.service';
import { CryptoService } from './cypto/crypto.service';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { GTMEvents } from '../../model/google-tag-manager/api-status';
import { IDENTITY_TOKEN } from '../interceptors/http-request.interceptor';

declare global {
  interface Window {
    AsappChatLoad: (config: AsappChatConfig, callback: (config: AsappChatConfig) => void) => void;
    AsappSetCustomer: (customerId: string, token: string, customerInfo: AsappCustomerInfo) => void;
    AsappRefresh: () => void;
    AsappShow: () => void;
    AsappHide: () => void;
    AsappUnloadAndClearSession: () => void;
    AsappGATrackingOnChatBtnClick: () => void;
    config: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AsappChatService {
  private _config: AsappChatConfig;
  private _cryptoService: CryptoService;
  private _oktaService: PGHOktaService;
  private _googleAnalyticsService: GoogleAnalyticsService;
  private _isInitialized = false;
  private customerInfo: AsappCustomerInfo;
  private router = inject(Router);
  private customerId: string;

  constructor() {}

  load(config: AsappChatConfig, cryptoService: CryptoService, oktaService: PGHOktaService, googleAnalyticsService: GoogleAnalyticsService): void {
    this._config = config;
    this._isInitialized = true;
    this._cryptoService = cryptoService;
    this._oktaService = oktaService;
    this._googleAnalyticsService = googleAnalyticsService;
  }

  setCustomerDetail(customerInfo: AsappCustomerInfo = null): void {
    if (!this._isInitialized || !this._config.Show) {
      console.log('ASAPP chat is not initialized or disabled');
      return;
    }

    // Create a copy of customer info or initialize if null
    const customerData: AsappCustomerInfo = customerInfo ? { ...customerInfo } : {};

    customerData.gaHashedPhone = this.hash(customerInfo.encrypted_phoneNumber);
    this.customerId = this.hash(customerInfo.encrypted_phoneNumber);

    const requests = [];
    const responseMap = new Map<number, string>();

    // Encrypt sensitive data if available
    if (customerData.encrypted_firstName) {
      responseMap.set(requests.length, 'encrypted_firstName');
      requests.push(this._cryptoService.rsaEncryptData(customerData.encrypted_firstName));
    }

    if (customerData.encrypted_lastName) {
      responseMap.set(requests.length, 'encrypted_lastName');
      requests.push(this._cryptoService.rsaEncryptData(customerData.encrypted_lastName));
    }

    if (customerData.encrypted_phoneNumber) {
      responseMap.set(requests.length, 'encrypted_phoneNumber');
      requests.push(this._cryptoService.rsaEncryptData(customerData.encrypted_phoneNumber));
    }

    if (requests.length > 0) {
      forkJoin(requests).subscribe((s) => {
        s.forEach((value: CryptoResponseDto, index) => {
          const propName = responseMap.get(index);
          customerData[propName] = value.encryptedData;
        });

        customerData.CompanySubDivision = this._config.Identity;
        customerData.domain = 'myaccount';

        this.customerInfo = customerData;
        this.setCustomer();
      });
    }
  }

  show(): void {
    if (!this.isInitialized || !this._config.Show) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    this.setCustomer();
    this._googleAnalyticsService.emitClickEvent(GTMEvents.ChatOpen, 'Live Support', undefined, undefined, undefined);
    window.AsappShow();
  }

  hide(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappHide();
  }

  unloadAndClearSession(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappUnloadAndClearSession();
  }

  refresh(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappRefresh();
  }

  get config(): AsappChatConfig {
    return this._config;
  }

  get isShow(): boolean {
    return window.config?.Asapp?.Show;
  }

  get isInitialized() {
    return (window.config?.Asapp?.IsInitialized ? window.config?.Asapp?.IsInitialized : false) && this._isInitialized;
  }

  private hash(data: string): string {
    const md = forge.md.sha256.create();
    md.update(data);
    return md.digest().toHex();
  }

  setCustomer() {
    this.customerInfo.url = this.router.url;
    this.customerInfo.virtualPageName = this._googleAnalyticsService.getActiveRouteTitle();
    const identityToken: any = localStorage.getItem(IDENTITY_TOKEN);
    window.AsappSetCustomer(this.customerId, identityToken, this.customerInfo);
  }
}
